/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectItem.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectItem - Карточка проекта с цветовым корешком и быстрыми действиями.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: карточка с цветовым корешком, hover/focus, группировка действий.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента и парный END_METHOD.
 * END_CHANGE_SUMMARY
 */

import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PeopleIcon from '@mui/icons-material/People';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ProjectAvatar from '../../../components/ProjectAvatar';
import { getColorByName } from '../../../utils/projectColors';
import { createInteractiveSurfaceTransition } from '../../../theme/appTheme';

/* START_METHOD_ProjectItem */
/* START_CONTRACT: ProjectItem
 * PURPOSE: Карточка проекта: навигация к задачам, быстрые ссылки и действия редактирования.
 * INPUTS: { project: Project, onEdit: (p)=>void, onDelete: (p)=>void }.
 * OUTPUTS: { JSX.Element - карточка }.
 * SIDE_EFFECTS: навигация по маршрутам; делегирует onEdit/onDelete.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: ProjectItem
 */
export default function ProjectItem({ project, onEdit, onDelete }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const accentColor = getColorByName(project.name);

  const createdDate = project.createdAt
    ? project.createdAt.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  const openFeatures = () => navigate(`/projects/${project.id}/features`);

  const handleCardKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFeatures();
    }
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  return (
    <Paper
      variant="outlined"
      onClick={openFeatures}
      onKeyDown={handleCardKeyDown}
      role="link"
      tabIndex={0}
      aria-label={`Открыть задачи проекта ${project.name}`}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: createInteractiveSurfaceTransition(theme, [
          'box-shadow',
          'border-color',
          'transform',
        ]),
        borderColor: alpha(accentColor, 0.2),
        '&:hover': {
          borderColor: alpha(accentColor, 0.45),
          boxShadow: `0 4px 20px ${alpha(accentColor, 0.12)}`,
          transform: 'translateY(-1px)',
          '& .project-card-arrow': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': {
          '&:hover': {
            transform: 'none',
          },
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accentColor,
        }}
      />

      <Box sx={{ pl: 2.5, pr: 2, pt: 2, pb: 1.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <ProjectAvatar name={project.name} size={40} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                noWrap
                sx={{ flex: 1, letterSpacing: '-0.01em' }}
              >
                {project.name}
              </Typography>
              <ArrowForwardIcon
                className="project-card-arrow"
                sx={{
                  fontSize: 18,
                  color: 'text.disabled',
                  opacity: 0,
                  transform: 'translateX(-4px)',
                  transition: createInteractiveSurfaceTransition(theme, ['opacity', 'transform']),
                  flexShrink: 0,
                }}
              />
            </Box>
            {createdDate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                Создан {createdDate}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: alpha(accentColor, 0.12) }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          bgcolor: alpha(accentColor, 0.03),
        }}
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Проектные роли">
            <IconButton
              size="small"
              aria-label="Проектные роли"
              onClick={() => navigate(`/projects/${project.id}/roles`)}
            >
              <GroupWorkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Сотрудники">
            <IconButton
              size="small"
              aria-label="Сотрудники"
              onClick={() => navigate(`/projects/${project.id}/employees`)}
            >
              <PeopleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Редактировать">
            <IconButton
              size="small"
              aria-label="Редактировать проект"
              onClick={() => onEdit(project)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Удалить">
            <IconButton
              size="small"
              color="error"
              aria-label="Удалить проект"
              onClick={() => onDelete(project)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}
/* END_METHOD_ProjectItem */
